import { IndexedEquipmentEntry } from 'app/pages/Equipment/EquipmentList/type';
import { InhibitRestartStatus } from 'app/pages/InhibitRestartPage/type';
import moment from 'moment';
import { IEquipmentEntry, SUBSCRIPTION_TYPE } from 'types/IEquipmentEntry';

type EquipmentFilter = (entry: IEquipmentEntry) => boolean;

const FILTERS_MAP: Record<string, EquipmentFilter> = {
  active_fault_codes: entry => !!entry.telematics?.faultCodes?.length,

  machine_running: entry => !!entry.telematics?.engineRunning,

  machine_in_transport: entry =>
    entry.telematics?.motionState === 'in-transport' ||
    (entry.telematics?.engineRunning === false &&
      entry.telematics?.motionState === 'moving'),

  restart_inhibited: entry =>
    entry.telematics?.restartInhibitStatus?.equipmentFeatureStatus ===
      InhibitRestartStatus.Enabled &&
    entry.telematics?.restartInhibitStatus?.equipmentStatus ===
      InhibitRestartStatus.RestartDisabled,

  basic_telematics_plan: entry =>
    !!entry.telematics &&
    !entry.subscriptions.some(
      ({ type }) =>
        type === SUBSCRIPTION_TYPE.PREMIUM_TELEMATICS ||
        type === SUBSCRIPTION_TYPE.INITIAL_TELEMATICS,
    ),

  premium_telematics_plan: entry =>
    entry.subscriptions.some(
      ({ type }) =>
        type === SUBSCRIPTION_TYPE.PREMIUM_TELEMATICS ||
        type === SUBSCRIPTION_TYPE.INITIAL_TELEMATICS,
    ),

  expiring_soon: entry => {
    const subscription =
      entry.subscriptions.find(
        sub => sub.type === SUBSCRIPTION_TYPE.PREMIUM_TELEMATICS,
      ) ||
      entry.subscriptions.find(
        sub => sub.type === SUBSCRIPTION_TYPE.INITIAL_TELEMATICS,
      );

    const endsOn = subscription?.endDate
      ? moment(subscription.endDate)
      : undefined;

    return endsOn
      ? endsOn.isSameOrAfter(moment()) &&
          endsOn.isSameOrBefore(moment().add(90, 'days'))
      : false;
  },
};

export default function filterEquipmentList(
  equipmentList: IEquipmentEntry[] = [],
  filterList: string[] = [],
  searchValue = '',
): IndexedEquipmentEntry[] {
  if (!filterList.length && !searchValue) {
    return equipmentList.map((entry, index) => ({
      equipment: entry,
      originalIndex: index,
    }));
  }

  const normalizedSearch = searchValue.trim().toLowerCase();

  return equipmentList.reduce<IndexedEquipmentEntry[]>((acc, entry, index) => {
    const filtersPassed = filterList.every(filterName => {
      const filterFn = FILTERS_MAP[filterName];
      return filterFn ? filterFn(entry) : true;
    });

    const searchPassed =
      !normalizedSearch ||
      entry?.nickName?.toLowerCase().includes(normalizedSearch) ||
      entry?.pinOrSerial?.toLowerCase().includes(normalizedSearch) ||
      entry?.model?.toLowerCase().includes(normalizedSearch);

    if (filtersPassed && searchPassed) {
      acc.push({ equipment: entry, originalIndex: index });
    }

    return acc;
  }, []);
}

/**
 * Reorders the equipment list so that smart attachments are placed
 * right before their associated machines (matched by serial).
 */
export function reorderSmartAttachments(
  equipmentList: IEquipmentEntry[] = [],
): IEquipmentEntry[] {
  const result: IEquipmentEntry[] = [];
  const attachmentsMap = new Map<string, IEquipmentEntry>();
  const seenSerials = new Set<string>();

  for (const entry of equipmentList) {
    if (entry.smartAttachment) {
      attachmentsMap.set(entry.serial, entry);
    }
  }

  for (const entry of equipmentList) {
    const attachedSerial = entry.telematics?.smartAttachment?.serial;
    const attachment = attachedSerial
      ? attachmentsMap.get(attachedSerial)
      : null;

    if (
      attachment &&
      attachment.serial !== entry.serial &&
      !seenSerials.has(attachment.serial)
    ) {
      result.push(attachment);
      seenSerials.add(attachment.serial);
    }

    if (!seenSerials.has(entry.serial)) {
      result.push(entry);
    }
  }

  return result;
}
